import React, { useState } from "react";

import { MenuItem } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import BadgeJobStatus from "Components/Generic/BadgeJobStatus";
import BadgeStatus from "Components/Generic/BadgeStatus";
import Skeleton from "Components/Generic/Skeleton";
import { useCustomerPortalContext } from "Context/CustomerPortalContext";
import { useCustomerPortalJobContext } from "Context/CustomerPortalJobContext";
import { useAppState } from "Context/StateContext";
import { useSecretManager } from "Hooks/useSecretManager";
import { useTranslation } from "react-i18next";
import { IoIosArrowDown } from "react-icons/io";
import { useHistory } from "react-router-dom";
import { CustomerPortalToken } from "Util/checkTokenCustomerInvite";
import env from "Util/env";
import { setWindowLocationWithTokenCustomerPortal } from "Util/setWindowLocationWithTokenCustomerPortal";

import useStyles, { Divider, StyledMenu, ContainerBadgeStatus, ButtonOrders } from "../styles";
import { JobSelection, JobSelectionContainer, ContentReferenceText } from "./styled";

export const CustomerPortalSelectionMenu = () => {
  const { secret } = useSecretManager(env.SECRET_ID);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { order, isLoading } = useCustomerPortalJobContext();
  const { ordersAssociated } = useCustomerPortalContext();

  const history = useHistory();
  const { currentCustomerUser } = useAppState();
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const formatOrder = (reference?: string, status?: string, mode?: string) => {
    return (
      <>
        {translate("order")} #{reference || ""} {mode ? `| ${translate(mode.toLowerCase())}` : null}
        <ContainerBadgeStatus>
          <BadgeJobStatus className={classes.badge} value={status} />
        </ContainerBadgeStatus>
      </>
    );
  };

  const handleOnClickMenuItem = (orderAssociated: any) => {
    if (!orderAssociated?.id) return;
    setAnchorEl(null);
    setWindowLocationWithTokenCustomerPortal(
      history,
      {
        jobId: orderAssociated.id,
        companyId: currentCustomerUser?.companyId,
        customerId: currentCustomerUser?.customerId,
      } as CustomerPortalToken,
      secret
    );
  };

  const getTitleLabel = () => {
    let label = translate("move");

    if (order?.reference) {
      label += ` #${order.reference}`;
    }
    if (order?.mode) {
      label += ` • ${translate(order.mode.toLowerCase())}`;
    }

    return label;
  };

  const renderMenuItems = () => {
    if (!ordersAssociated.length) return;

    return ordersAssociated
      .filter((orderAssociated) => orderAssociated?.id !== order?.id)
      .map((orderAssociated) => (
        <MenuItem key={orderAssociated.id} onClick={() => handleOnClickMenuItem(orderAssociated)}>
          {formatOrder(orderAssociated.reference, orderAssociated.status, orderAssociated.mode)}
        </MenuItem>
      ));
  };

  return (
    <>
      <Skeleton isLoading={isLoading} classesName={classes.orderSelectionLoading}>
        <JobSelection>
          <JobSelectionContainer>
            <ContentReferenceText>{getTitleLabel()}</ContentReferenceText>

            <BadgeStatus value={order?.status} />
          </JobSelectionContainer>

          {ordersAssociated.length > 1 && (
            <ButtonOrders
              open={Boolean(anchorEl)}
              onClick={(event) => setAnchorEl(event.currentTarget)}
            >
              <IoIosArrowDown size={20} color={colors.gray600} />
            </ButtonOrders>
          )}
        </JobSelection>
      </Skeleton>

      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          className: classes.paperMenu,
        }}
      >
        <MenuItem selected onClick={() => handleOnClickMenuItem(order)}>
          {formatOrder(order?.reference, order?.status, order?.mode)}
        </MenuItem>

        <Divider />
        {renderMenuItems()}
      </StyledMenu>
    </>
  );
};
